import React, { useMemo, useState } from 'react';
import { Search, Plus, HardHat, Pencil, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useFetch, Spinner } from '../lib/useFetch';
import { dpiApi, workersApi, companiesApi } from '../lib/api';
import { useCrudActions } from '../lib/useCrudActions';
import EntityFormModal from '../components/EntityFormModal';
import { dpiFields } from '../lib/entitySchemas';
import { fmtDate } from '../lib/format';
import { statusBadge } from './FormazionePage';
import { useAuth } from '../context/AuthContext';
import SortAZButton from '../components/SortAZButton';

const DpiPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const { data: dpi, loading, reload } = useFetch(() => dpiApi.list(), []);
  const { data: workers } = useFetch(() => workersApi.list(), []);
  const { data: companies } = useFetch(() => companiesApi.list(), []);
  const crud = useCrudActions({ api: dpiApi, label: 'DPI', onChange: reload });

  const workerOf = (id) => workers?.find((ww) => ww.id === id);
  const workerName = (id) => {
    const w = workerOf(id);
    return w ? `${w.firstName} ${w.lastName}` : '—';
  };
  const companyName = (id) => companies?.find((c) => c.id === id)?.name || '—';

  const filtered = useMemo(() => {
    let list = dpi || [];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((d) => `${d.tipo || ''} ${workerName(d.workerId)}`.toLowerCase().includes(q));
    }
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...list].sort((a, b) => {
      const wa = workerOf(a.workerId);
      const wb = workerOf(b.workerId);
      const la = (wa?.lastName || '').localeCompare(wb?.lastName || '', 'it', { sensitivity: 'base' });
      if (la !== 0) return la * dir;
      const fa = (wa?.firstName || '').localeCompare(wb?.firstName || '', 'it', { sensitivity: 'base' });
      return fa * dir;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, dpi, workers, sortDir]);

  if (loading) return <Spinner />;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#fef7d4] text-[#8a6608] flex items-center justify-center"><HardHat size={20} /></div>
          <h2 className="text-lg font-bold text-gray-800">Dispositivi di Protezione Individuale</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SortAZButton
            direction={sortDir}
            onToggle={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            testId="dpi-sort-az"
            size="md"
          />
          <button onClick={() => crud.onCreate({ stato: 'consegnato' })} className="ro-hide px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#E6B800] flex items-center gap-2"><Plus size={14} /> NUOVA CONSEGNA</button>
        </div>
      </div>

      <div className="relative max-w-md mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca DPI o lavoratore..." className="pl-9 h-10 border-gray-200" />
      </div>

      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-gray-500">
              <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Tipo DPI</th>
              <th className="text-left bg-gray-100 px-4 py-3">Lavoratore</th>
              <th className="text-left bg-gray-100 px-4 py-3">Azienda</th>
              <th className="text-left bg-gray-100 px-4 py-3">Consegna</th>
              <th className="text-left bg-gray-100 px-4 py-3">Scadenza</th>
              <th className="text-left bg-gray-100 px-4 py-3">Taglia</th>
              <th className="text-left bg-gray-100 px-4 py-3">Stato</th>
              <th className="bg-gray-100 rounded-r-xl px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                <td className="px-4 py-3 font-semibold text-gray-800">{d.tipo}</td>
                <td className="px-4 py-3 text-gray-700">{workerName(d.workerId)}</td>
                <td className="px-4 py-3 text-gray-600 text-xs">{companyName(d.companyId)}</td>
                <td className="px-4 py-3 text-gray-700">{fmtDate(d.dataConsegna)}</td>
                <td className="px-4 py-3 text-gray-700">{fmtDate(d.scadenza)}</td>
                <td className="px-4 py-3 text-gray-700">{d.taglia || '—'}</td>
                <td className="px-4 py-3">{statusBadge(d.stato)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => crud.onEdit(d)} title="Modifica" className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><Pencil size={13} className="text-gray-600" /></button>
                    <button onClick={() => crud.onDelete(d)} title="Elimina" className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><X size={14} className="text-red-600" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-10 text-center text-sm text-gray-400">Nessun DPI trovato</div>}
      </div>

      <EntityFormModal
        open={crud.open}
        onOpenChange={(o) => { if (!o) crud.closeModal(); }}
        title={crud.editing?.id ? 'Modifica DPI' : 'Nuova consegna DPI'}
        fields={dpiFields(workers || [], companies || [], isClient)}
        initialValues={crud.editing || {}}
        onSubmit={crud.save}
      />
    </div>
  );
};

export default DpiPage;
